import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  DeviceEventEmitter,
  Image,
  ImageBackground,
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  InteractionManager,
  Text,
  Dimensions,
  ScrollView,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';

import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';
import { getListFromPages } from '~components/HomeScreenComponent/helpers/getListFromPages';

import { useSimplifiedMapEvents } from '~hooks/event/useSimplifiedMapEvents';
import { useListViewEvents } from '~hooks/event/useListViewEvents';
import { useHomeStore } from '~providers/home/zustand';
import { Event } from '~types/api/event';
import { EVENT_AGE_GROUP, ORDER_BY, ORDER_DIR, TABS } from '~types/events';
import { responsive, spacing } from '~constants';
import { getMapStyle } from '~constants/locationMap';
import ModernEventsList from '../components/ModernEventsList';
import { Marker, PROVIDER_GOOGLE, Region } from 'react-native-maps';
// import BottomSheet from '@gorhom/bottom-sheet'; // Removed - using conditional rendering instead
import { NavigationProps } from '~types/navigation/navigation.type';
import { SCREENS } from '~constants';
import { useMapEventPrefetch } from '~hooks/performance/useMapEventPrefetch';
import LikeButton from '~components/LikeButton';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '~providers/userStore/zustand';
import { useGetBusinessAccount } from '~hooks/business/useGetBusinessAccount';
import { useUpdateUser } from '~hooks/user/useUpdateUser';
import useUpdateBusiness from '~hooks/business/useUpdateBusiness';
import useGetCurrentPosition from '~components/LocationModal/hooks/useGetCurrentPosition';
// React 19 compatible ClusteredMapView
import MapView from 'react-native-maps';
import FastImage from 'react-native-fast-image';
import EventDetailsBottomSheet from './EventDetailsBottomSheet';
import { useMapsContext } from '~providers/maps/zustand';
import { useTheme } from '~contexts/ThemeContext';

import ModernEventPin from '~assets/icons/ModernEventPin';
import {
  runOnUIThread,
  safeEmit,
  safeMapAnimation,
  // safeBottomSheetOperation, // Removed - no longer using BottomSheet
  createSafeEventListener,
} from '~Utils/uiThreadSafety';
import ModernSpinner from '~components/ModernSpinner';
import AnimatedPin from './AnimatedPin';
import Modal from 'react-native-modal';
import moment from 'moment';

// Import QuickFilters to use as ListHeaderComponent
import QuickFilters from '~components/HomeScreenComponent/QuickFilters/QuickFilters';
import SubscriptionReminderTile from '~components/HomeScreenComponent/SubscriptionReminderTile';

const haversine = require('haversine');

interface HomeContentInterface {
  scrollHandler: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  filterEvents: (data: any, selectedTimeframe: { id: number; title: string }) => any;
  selectedTimeframe: { id: number; title: string };
  globalInputValue: string;
  isFree?: boolean;
  isNeighbourhood?: boolean;
  isPyxiSelect?: boolean;
  isPyxiCore?: boolean;
  isMapView: boolean;
  setIsMapView: (value: boolean) => void;
  eventServiceType?: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | null;
  // QuickFilters props
  onServiceTypeChange?: (serviceType: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | null) => void;
  selectedServiceType?: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | null;
  // Timeframe props for QuickFilters
  timeframes?: Array<{id: number; title: string}>;
  onTimeframeSelect?: (timeframe: {id: number; title: string}) => void;
}

// Memoized InfluencerPin for influencer event type
interface InfluencerPinProps {
  uri: string;
  styles: any;
}
const InfluencerPin = React.memo(({ uri, styles }: InfluencerPinProps) => (
  <View style={styles.pinContainer}>
    <View style={styles.pinCircle}>
      <FastImage source={{ uri }} style={styles.pinImage} />
    </View>
    <View style={styles.pinTriangle} />
  </View>
));

// Memoized EventMarker component
interface EventMarkerProps {
  event: any;
  isSelected: boolean;
  styles: any;
}
const EventMarker = React.memo(({ event, isSelected, styles }: EventMarkerProps) => (
  <>
    {event.event_type === 'influencer' ? (
      <InfluencerPin styles={styles} uri={event.host_photo} />
    ) : (
      <AnimatedPin isSelected={isSelected} eventType={event.event_type} hostId={event.host_id} />
    )}
  </>
));

const HomeContent: React.FC<HomeContentInterface> = ({
  scrollHandler,
  filterEvents,
  selectedTimeframe,
  globalInputValue,
  isFree,
  isNeighbourhood,
  isPyxiSelect,
  isPyxiCore,
  isMapView,
  setIsMapView,
  eventServiceType,
  // QuickFilters props
  onServiceTypeChange,
  selectedServiceType,
  timeframes,
  onTimeframeSelect,
}) => {
  console.log('🏠 [HOME CONTENT] Component is rendering!', { 
    isMapView, 
    isNeighbourhood, 
    eventServiceType: eventServiceType,
    selectedTimeframe: selectedTimeframe?.title 
  });
  const { colors, isDarkMode } = useTheme();
  const { radius, isForKids } = useHomeStore();
  const navigation = useNavigation<NavigationProps>();
  // Use Google's built-in light/dark themes via userInterfaceStyle prop

  const styles = useMemo(
    () =>
      StyleSheet.create({
        pinContainer: {
          alignItems: 'center',
          overflow: 'visible',
        },
        pinCircle: {
          width: 40, // Reduced from 60 to 40 (will appear as ~52 with 1.3x scale)
          height: 40, // Reduced from 60 to 40
          borderRadius: 20, // Reduced from 30 to 20
          borderWidth: 2, // Reduced from 3 to 2
          borderColor: colors.black,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: colors.white,
        },
        pinImage: {
          width: 36, // Reduced from 54 to 36 (will appear as ~47 with 1.3x scale)
          height: 36, // Reduced from 54 to 36
          borderRadius: 18, // Reduced from 27 to 18
        },
        pinTriangle: {
          width: 0,
          height: 0,
          borderLeftWidth: 8, // Reduced from 12 to 8 (will appear as ~10 with 1.3x scale)
          borderRightWidth: 8, // Reduced from 12 to 8
          borderTopWidth: 12, // Reduced from 18 to 12 (will appear as ~16 with 1.3x scale)
          borderLeftColor: 'transparent',
          borderRightColor: 'transparent',
          borderTopColor: colors.black,
          marginTop: -3,
        },

        iconContainer: {
          zIndex: 100,
          backgroundColor: colors.overlayBackground,
          width: 35,
          height: 35,
          borderRadius: 35,
          position: 'absolute',
          top: 5,
          right: 10,
          flexDirection: 'row',
          gap: 10,
        },
        imageEvent: {
          width: 300,
          height: 100,
          borderRadius: 10,
        },
        dateRangeContainer: {
          zIndex: 1,
          position: 'absolute',
          top: 10,
          left: 10,
          backgroundColor: colors.overlayBackground,
          borderRadius: 10,
          padding: 6,
        },
        eventTitle: {
          fontWeight: 'bold',
          color: colors.white,
          fontSize: 10,
          position: 'absolute',
          bottom: 20,
          left: 10,
          zIndex: 100,
        },
        eventCard: {
          height: 100,
          width: 300,
          alignSelf: 'center',
          borderRadius: 10,
          zIndex: 1909000,
        },
        calloutWrapper: {
          alignItems: 'center',
        },
        calloutContainer: {
          width: 200,
          backgroundColor: colors.white + 'B3',
          borderRadius: 8,
          paddingHorizontal: 8,
          paddingVertical: 3,
        },
        calloutTitle: {
          fontWeight: 'bold',
          fontSize: 10,
        },
        calloutDescription: {
          fontSize: 9,
          color: 'orange',
          position: 'absolute',
          bottom: 7,
          left: 10,
          zIndex: 100,
        },
        arrow: {
          width: 0,
          height: 0,
          borderLeftWidth: 10,
          borderRightWidth: 10,
          borderTopWidth: 10,
          borderLeftColor: 'transparent',
          borderRightColor: 'transparent',
          alignSelf: 'center',
          borderTopColor: colors.white + '4D',
          marginTop: -1,
        },
      }),
    [colors],
  );
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { data: businessAccountData } = useGetBusinessAccount(user?.uid);
  const { getCurrentPosition } = useGetCurrentPosition();

  // Get current position state first
  const { currentPositionState, setCurrentPositionState } = useMapsContext();

  // Request location permissions when map view is shown (only once)
  useEffect(() => {
    if (isMapView && !currentPositionState) {
      console.log('🗺️ [MAP] Map view is active, requesting location permissions');
      // Only request location permissions if we don't have position yet
      getCurrentPosition(true, false);
    }
  }, [isMapView]); // Remove getCurrentPosition and currentPositionState from dependencies to prevent infinite loop

  // Use simplified map events with coordinate-based caching for map view

  const {
    data: mapData,
    isLoading: isMapLoading,
    isFetching: isMapFetching,
    fetchNextPage: mapFetchNextPage,
    hasNextPage: mapHasNextPage,
    isFetchingNextPage: isMapFetchingNextPage,
    refetch: mapRefetch,
    remove: mapRemove,
    updateMapViewport,
    currentRadius: mapCurrentRadius,
    clearCache: clearMapCache,
    getCacheStats: getMapCacheStats,
  } = useSimplifiedMapEvents({
    radius,
    isForKids,
    selectedTimeframe,
    globalInputValue,
    isNeighbourhood: isNeighbourhood || false,
    isPyxiSelect: isPyxiSelect || false,
    isPyxiCore: isPyxiCore || false,
    userLocation: currentPositionState,
    enabled: isMapView && !!currentPositionState, // Only fetch when in map view AND have location
    eventServiceType, // Pass the event service type
  });

  // Use list view events with dynamic radius expansion for list view
  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    fetchNextPage: listFetchNextPage,
    hasNextPage: listHasNextPage,
    isFetchingNextPage: isListFetchingNextPage,
    refetch: listRefetch,
    remove: listRemove,
    currentRadius,
    expandRadius,
    resetRadius,
    isExpanding,
    eventCount,
    canLoadMore,
    clearCache: clearListCache,
    getCacheStats: getListCacheStats,
  } = useListViewEvents({
    initialRadius: radius,
    isForKids,
    selectedTimeframe,
    globalInputValue,
    isNeighbourhood: isNeighbourhood || false,
    isPyxiSelect: isPyxiSelect || false,
    isPyxiCore: isPyxiCore || false,
    userLocation: currentPositionState,
    enabled: !isMapView && !!currentPositionState, // Only fetch when in list view AND have location
    eventServiceType, // Pass the event service type
  });

  // Note: Regular events hook removed - now using specialized hooks for map and list views

  // Use appropriate data source based on view mode
  const data = isMapView ? mapData : listData;
  const isLoading = isMapView ? isMapLoading : isListLoading;
  const isFetching = isMapView ? isMapFetching : isListFetching;
  const fetchNextPage = isMapView ? mapFetchNextPage : listFetchNextPage;
  const hasNextPage = isMapView ? mapHasNextPage : listHasNextPage;
  const isFetchingNextPage = isMapView ? isMapFetchingNextPage : isListFetchingNextPage;
  const refetch = isMapView ? mapRefetch : listRefetch;
  const remove = isMapView ? mapRemove : listRemove;

  const { top } = useSafeAreaInsets();
  const { width, height } = useSafeAreaFrame();
  const isFocused = useIsFocused();
  // const sheetRef = useRef<BottomSheet>(null); // Removed - no longer using BottomSheet
  // Removed BottomSheet snap points - using direct view switching instead
  // const snapPoints = useMemo(() => ['1%', '100%'], []);
  // const [currentPosition, setCurrentPosition] = useState(0);
  // Extract events from paginated data with proper handling of API response structure
  const eventsList = useMemo(() => {
    if (!data?.pages) {
      return [];
    }

    const events = data.pages.flatMap(page => {
      // Handle different response structures
      if (page?.items && Array.isArray(page.items)) {
        return page.items;
      } else if (page?.data && Array.isArray(page.data)) {
        return page.data;
      } else if (Array.isArray(page)) {
        return page;
      }
      return [];
    });

    return events;
  }, [data, isMapView]);

  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const mapRef = useRef<any>(null);
  const { mutateAsync: updateUserMutation } = useUpdateUser();
  const { mutateAsync: updateBusinessMutation } = useUpdateBusiness();
  const [prevRegion, setPrevRegion] = useState<Region | null>(null);
  const lastRegionRef = useRef<Region | null>(null); // Store the last region to compare

  // Helper function to check if two regions are effectively the same
  const areRegionsEqual = (region1: Region | null, region2: Region | null): boolean => {
    if (!region1 || !region2) {
      return false;
    }
    const latDiff = Math.abs(region1.latitude - region2.latitude);
    const lonDiff = Math.abs(region1.longitude - region2.longitude);
    return latDiff < 0.0001 && lonDiff < 0.0001; // Adjust threshold as needed
  };

  // Update map only when currentPositionState changes significantly and map is visible
  useEffect(() => {
    if (isMapView && currentPositionState && currentPositionState.latitude && mapRef.current) {
      const newRegion: Region = {
        latitude: currentPositionState.latitude,
        longitude: currentPositionState.longitude,
        latitudeDelta: 0.04,
        longitudeDelta: 0.04,
      };

      // Only update if the new region is different from the last known region
      if (!areRegionsEqual(newRegion, lastRegionRef.current)) {
        console.log('🗺️ [MAP] Centering map on user location:', currentPositionState);
        // Ensure map update happens on UI thread - no animation for instant loading
        safeMapAnimation(mapRef, map => {
          map.animateToRegion(newRegion, 0); // Instant update with no animation
          lastRegionRef.current = newRegion; // Update last known region
        });
      }
    }
  }, [isMapView, currentPositionState?.latitude, currentPositionState?.longitude]);

  /*  useEffect(() => {
    if (isNeighbourhood && eventsList && eventsList.length > 0 && mapRef.current) {
      const event = eventsList[eventsList.length - 1]; // Get the last event
      const newRegion: Region = {
        latitude: event.coords.lat,
        longitude: event.coords.long,
        latitudeDelta: 0.015,
        longitudeDelta: 0.015,
      };

      // Only animate if the new region is different from the last known region
      if (!areRegionsEqual(newRegion, lastRegionRef.current)) {
        mapRef.current.animateToRegion(newRegion);
        lastRegionRef.current = newRegion; // Update last known region
      }
    }
  }, [eventsList, isNeighbourhood]) */

  // Focus effect for additional refetching
  useFocusEffect(
    React.useCallback(() => {
      setSelectedEvent(null);
    }, []),
  ),
    useEffect(() => {
      if (eventsList && globalInputValue) {
        safeEmit('onSearchResult', { events: eventsList });
      }
    }, [eventsList, globalInputValue]);

  useEffect(() => {
    if (!isMapView) {
      remove();
      refetch();
      console.log('refetchrefetchrefetchrefetch');
    }
    const listener = DeviceEventEmitter.addListener('eventSelection', data => {
      const item = data.event;
      if (item) {
        navigation.navigate(SCREENS.HOME_EVENT, {
          tag: `${item.event_id}=tag`,
          eventId: item.event_id,
          item: item,
        });
      }
    });

    return () => {
      listener.remove();
    };
  }, [globalInputValue]);

  useEffect(() => {
    // Always request location on mount to have it ready for map view
    if (!currentPositionState) {
      getCurrentPosition(true, false);
    }
  }, []);

  useEffect(() => {
    const eventListener = DeviceEventEmitter.addListener('homeLocationChange', data => {
      runOnUIThread(() => {
        // Removed BottomSheet operation - no longer needed
        // safeBottomSheetOperation(sheetRef, sheet => {
        //   sheet.snapToIndex(0);
        // });

        if (data.lat && data.long) {
          const newRegion: Region = {
            latitude: data.lat,
            longitude: data.long,
            latitudeDelta: 0.04,
            longitudeDelta: 0.04,
          };
          handleLocationChange({
            latitude: data.lat,
            longitude: data.long,
            latitudeDelta: 0.04,
            longitudeDelta: 0.04,
          });
          if (!areRegionsEqual(newRegion, lastRegionRef.current)) {
            safeMapAnimation(mapRef, map => {
              map.animateToRegion(newRegion);
              lastRegionRef.current = newRegion;
            });
          }
        }
      });
    });

    return () => {
      eventListener.remove();
      // Cleanup throttle timeout on unmount
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      runOnUIThread(() => {
        setSelectedEvent(null);
      });
    });

    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => { });

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  useEffect(() => {
    remove();
    refetch();
  }, [radius, isForKids, selectedTimeframe, isNeighbourhood]);

  // Refetch data when eventServiceType changes
  useEffect(() => {
    console.log('🔄 [HOME CONTENT] eventServiceType useEffect triggered:', {
      eventServiceType,
      defined: eventServiceType !== undefined,
      isNeighbourhood,
      isPyxiSelect,
      selectedTimeframe: selectedTimeframe?.title
    });
    if (eventServiceType !== undefined) {
      console.log('🔄 [HOME CONTENT] eventServiceType changed to:', eventServiceType, '- triggering refetch');
      remove();
      refetch();
    }
  }, [eventServiceType, remove, refetch]);

  // Track all prop changes for debugging
  useEffect(() => {
    console.log('🔍 [HOME CONTENT] Props changed:', {
      eventServiceType,
      isPyxiSelect,
      selectedTimeframe: selectedTimeframe?.id,
      timeframeTitle: selectedTimeframe?.title
    });
  }, [eventServiceType, isPyxiSelect, selectedTimeframe]);

  // Removed BottomSheet position control - using direct view switching instead
  // useEffect(() => {
  //   const targetIndex = isMapView ? 0 : 1;
  //   if (currentPosition !== targetIndex) {
  //     safeBottomSheetOperation(sheetRef, sheet => {
  //       sheet.snapToIndex(targetIndex);
  //     });
  //   }
  // }, [isMapView, currentPosition]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      console.log(`📄 [HOME CONTENT] Loading more events (${isMapView ? 'map' : 'list'} view)`);
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isMapView]);

  // Removed handleSheetChange - no longer using BottomSheet
  // const handleSheetChange = useCallback((index: number) => {
  //   console.log('handleSheetChange', index);
  //   runOnUIThread(() => {
  //     setSelectedEvent(null);
  //     setCurrentPosition(index);
  //   });
  // }, []);

  // Create a stable position reference that only updates on significant changes
  // Use a ref to store the last stable position to prevent unnecessary recalculations
  const lastStablePositionRef = useRef<{ latitude: number; longitude: number } | null>(null);

  const stablePosition = useMemo(() => {
    if (!currentPositionState) return null;

    const roundedPosition = {
      latitude: Math.round(currentPositionState.latitude * 100) / 100, // Round to 2 decimal places (~1km precision)
      longitude: Math.round(currentPositionState.longitude * 100) / 100,
    };

    // Only update if the position has changed significantly (more than 5km)
    if (
      !lastStablePositionRef.current ||
      Math.abs(lastStablePositionRef.current.latitude - roundedPosition.latitude) >= 0.05 ||
      Math.abs(lastStablePositionRef.current.longitude - roundedPosition.longitude) >= 0.05
    ) {
      lastStablePositionRef.current = roundedPosition;
    }

    return lastStablePositionRef.current;
  }, [currentPositionState?.latitude, currentPositionState?.longitude]);

  const filteredEvents = useMemo(() => {
    let events = eventsList;

    // Ensure events is always an array
    if (!events || !Array.isArray(events)) {
      return [];
    }

    // Apply time-based filtering first
    if (events && filterEvents) {
      events = filterEvents(events, selectedTimeframe);
      // Ensure filterEvents didn't return null/undefined
      if (!events || !Array.isArray(events)) {
        return [];
      }
    }

    // Then apply free events filter if needed
    if (isFree && events) {
      events = events.filter(event => event.is_paid == false);
    }

    return events;
  }, [eventsList, isFree, filterEvents, selectedTimeframe, isNeighbourhood, radius, stablePosition]);

  // Map event prefetching - prefetch when 10 or fewer events
  const { prefetchEvent, isPrefetched, getPrefetchStats } = useMapEventPrefetch({
    events: filteredEvents || [],
    enabled: isMapView && (filteredEvents?.length || 0) <= 10,
    maxEventsToPrefetch: 10,
    prefetchDelay: 1000,
    prefetchStrategy: 'delayed',
  });

  // Helper to group events by coordinates
  const groupEventsByLocation = (events: Event[]): { key: string; lat: number; long: number; events: Event[] }[] => {
    const groups: Record<string, Event[]> = {};
    events.forEach((event: Event) => {
      const key = `${event.coords.lat.toFixed(6)},${event.coords.long.toFixed(6)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });
    return Object.entries(groups).map(([key, events]) => ({
      key,
      lat: events[0].coords.lat,
      long: events[0].coords.long,
      events,
    }));
  };

  const [eventListModalVisible, setEventListModalVisible] = useState(false);
  const [modalEvents, setModalEvents] = useState<Event[]>([]);

  // Memoize grouped locations
  const groupedLocations = useMemo(() => groupEventsByLocation(filteredEvents), [filteredEvents]);

  // Memoize the markers to prevent unnecessary re-renders
  const memoizedMarkers = useMemo(() => {
    return groupedLocations.map(group => {
      const isSelected = group.events.some(e => selectedEvent?.event_id === e.event_id);
      const isCluster = group.events.length > 1;
      return (
        <Marker
          coordinate={{ latitude: group.lat, longitude: group.long }}
          key={group.key}
          onPress={() => {
            if (isCluster) {
              setModalEvents(group.events);
              setEventListModalVisible(true);
            } else {
              const event = group.events[0];
              if (selectedEvent?.event_id !== event.event_id) {
                runOnUIThread(() => {
                  Keyboard.dismiss();
                  setSelectedEvent(event);
                  if (!isPrefetched(event.event_id)) {
                    prefetchEvent(event.event_id);
                  }
                });
              }
            }
          }}
          tracksViewChanges={isSelected}
          style={{ overflow: 'visible', zIndex: 1000 }}>
          {isCluster ? (
            <View style={{ alignItems: 'center' }}>
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: colors.eventBusiness,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderWidth: 2,
                  borderColor: colors.white,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.2,
                  shadowRadius: 2,
                  elevation: 3,
                }}>
                <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{group.events.length}</Text>
              </View>
              {/* Pin tail for cluster pin */}
              <View
                style={{
                  width: 0,
                  height: 0,
                  borderLeftWidth: 8,
                  borderRightWidth: 8,
                  borderTopWidth: 12,
                  borderLeftColor: 'transparent',
                  borderRightColor: 'transparent',
                  borderTopColor: colors.eventBusiness,
                  marginTop: -2,
                }}
              />
            </View>
          ) : (
            <EventMarker
              styles={styles}
              key={group.events[0].event_id}
              event={group.events[0]}
              isSelected={isSelected}
            />
          )}
        </Marker>
      );
    });
  }, [groupedLocations, selectedEvent, isPrefetched, prefetchEvent, colors, styles]);

  useEffect(() => {
    if (isMapView && filteredEvents) {
      console.log(`🗺️ [PIN RENDER] Rendering ${filteredEvents.length} pins on map`);
    }
  }, [filteredEvents, isMapView]);

  const handleLocationChange = useCallback(
    async (location: Region | null) => {
      if (!location) {
        return;
      }

      try {
        // Emit loading start event for search bar - ensure it's on UI thread
        safeEmit('mapLoadingStart');

        if (businessAccountData?.uid) {
          const payload = { uid: user?.uid, coords: { lat: location.latitude, long: location.longitude } };
          await updateBusinessMutation(payload);
          return;
        }

        await updateUserMutation({
          coords_real: { lat: location.latitude, long: location.longitude },
        });

        // Ensure refetch happens on UI thread
        runOnUIThread(() => {
          setTimeout(() => {
            refetch();
          }, 200);
        });
      } catch (error) {
        console.log(error);
      }
    },
    [businessAccountData, updateBusinessMutation, updateUserMutation, refetch, user?.uid],
  );

  // Add throttling to prevent excessive region change updates
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRegionChangeComplete = useCallback(
    (newRegion: Region) => {
      // Store the exact region from onRegionChangeComplete
      lastRegionRef.current = newRegion;

      // Clear any existing timeout
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      // Throttle the actual state updates to prevent excessive re-renders - reduced delay for smoother pin loading
      throttleTimeoutRef.current = setTimeout(() => {
        // Ensure UI updates happen on main thread
        runOnUIThread(() => {
          Keyboard.dismiss();
          safeEmit('hideSuggestion');

          // Update state only if the region has changed significantly
          if (!areRegionsEqual(newRegion, prevRegion)) {
            // Update viewport for event fetching when in map view
            if (isMapView && updateMapViewport) {
              updateMapViewport(newRegion);
            }

            // Only update position state if it's a significant change (>1000km)
            const currentDistance = prevRegion
              ? haversine(
                { latitude: prevRegion.latitude, longitude: prevRegion.longitude },
                { latitude: newRegion.latitude, longitude: newRegion.longitude },
                { unit: 'km' },
              )
              : Infinity;

            if (currentDistance >= 1000 || !prevRegion) {
              setCurrentPositionState({
                latitude: newRegion.latitude,
                longitude: newRegion.longitude,
                address: '',
                country: currentPositionState?.country,
              });
            }

            // Only update user's permanent location for significant moves (>= 1000km)
            if (currentDistance >= 1000 || !prevRegion) {
              handleLocationChange(newRegion);
              setPrevRegion(newRegion);
            }
          }
        });
      }, 150); // 150ms throttle - reduced for smoother pin loading
    },
    [prevRegion, handleLocationChange, setCurrentPositionState, isMapView, updateMapViewport, currentPositionState],
  );

  useEffect(() => {
    safeEmit('eventSelection', { event: selectedEvent });
  }, [selectedEvent]);

  // Emit loading end event when loading states change
  useEffect(() => {
    if (!isLoading && !isFetching) {
      safeEmit('mapLoadingEnd');
    }
  }, [isLoading, isFetching]);

  // Create ListHeaderComponent with QuickFilters
  const ListHeaderComponent = useMemo(() => {
    return (
      <View style={{ backgroundColor: colors.background }}>
        <SubscriptionReminderTile />
        <QuickFilters
          selectedTimeframe={selectedTimeframe}
          onTimeframeSelect={onTimeframeSelect || (() => {})}
          isMapView={isMapView}
          setIsMapView={setIsMapView}
          onServiceTypeChange={onServiceTypeChange}
          selectedServiceType={selectedServiceType}
        />
      </View>
    );
  }, [
    selectedTimeframe, 
    onTimeframeSelect, 
    isMapView, 
    setIsMapView, 
    onServiceTypeChange, 
    selectedServiceType,
    colors.background
  ]);

  return (
    <View style={{ flex: 1, overflow: 'visible' }}>
      <View style={{ flex: 1, overflow: 'visible' }}>
        {isMapView ? (
          currentPositionState ? (
            <View style={{ flex: 1, overflow: 'visible' }}>
              {/* Ensure header renders above the scaled map container */}
              <View style={{ zIndex: 2, position: 'relative' }}>{ListHeaderComponent}</View>
              <View
                style={{
                  flex: 1,
                  overflow: 'visible',
                  transform: [{ scale: 2 }], // Increased to 2x scaling for maximum size
                  // Adjust margins to compensate for 2x scaling
                  marginTop: -90,
                  marginBottom: -100,
                  marginLeft: -100,
                  marginRight: -100,
                  zIndex: 1,
                }}>
                <MapView
                ref={mapRef}
                style={{ flex: 1 }}
                provider={PROVIDER_GOOGLE}
                showsUserLocation={true}
                onCalloutPress={e => {
                  console.log(e, 'onCalloutPressonCalloutPress');
                }}
                initialRegion={{
                  latitude: currentPositionState.latitude,
                  longitude: currentPositionState.longitude,
                  latitudeDelta: 0.04, // smaller value for more zoom
                  longitudeDelta: 0.04,
                }} // Detect taps outside markers
                onRegionChangeComplete={handleRegionChangeComplete}>
                {memoizedMarkers}
                </MapView>
                {/* Modal for event list at a location */}
                <Modal
                isVisible={eventListModalVisible}
                onBackdropPress={() => setEventListModalVisible(false)}
                onBackButtonPress={() => setEventListModalVisible(false)}
                style={{ justifyContent: 'flex-end', margin: 0 }}>
                <View
                  style={{
                    backgroundColor: colors.background,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    padding: 16,
                    maxHeight: Dimensions.get('window').height * 0.55,
                  }}>
                  <Text style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12, color: colors.textPrimary }}>
                    Events at this location
                  </Text>
                  <ScrollView showsVerticalScrollIndicator={false}>
                    {modalEvents.map(event => (
                      <TouchableOpacity
                        key={event.event_id}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          paddingVertical: 10,
                          borderBottomWidth: 1,
                          borderBottomColor: colors.border || '#eee',
                        }}
                        onPress={() => {
                          setEventListModalVisible(false);
                          setTimeout(() => {
                            setSelectedEvent(event);
                          }, 500);
                        }}>
                        <FastImage
                          source={{ uri: event.image_url }}
                          style={{ width: 44, height: 44, borderRadius: 8, marginRight: 12, backgroundColor: '#eee' }}
                          resizeMode={FastImage.resizeMode.cover}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={{ color: colors.textPrimary, fontSize: 14, fontWeight: '600' }}>{event.name}</Text>
                          <Text style={{ color: colors.textSecondary, fontSize: 10, marginTop: 2 }}>
                            {moment(event.start_date).format('ddd, MMM DD, YYYY hh:mm A')} -{' '}
                            {moment(event.end_date).format('ddd, MMM DD, YYYY hh:mm A')}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                  </View>
                </Modal>
              </View>
            </View>
          ) : (
            <ModernSpinner style={{ alignSelf: 'center', marginVertical: 5 }} />
          )
        ) : (
          <View style={{ flex: 1, backgroundColor: colors.background }}>
            {isListLoading || (isListFetching && <ModernSpinner style={{ alignSelf: 'center', marginVertical: 5 }} />)}
            <ModernEventsList
              data={filteredEvents}
              isLoading={isLoading}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              refreshing={refreshing}
              onRefresh={() => refetch()}
              onEndReached={handleLoadMore}
              onEndReachedThreshold={0.5}
              isMyEvents={false}
              emptyStateTitle="No events found"
              emptyStateSubtitle="Try adjusting your search or location to find events near you!"
              estimatedItemSize={280}
              ListHeaderComponent={ListHeaderComponent}
              contentContainerStyle={{
                paddingBottom: responsive.getValue({ xs: 80, sm: 100, md: 120 }, 100),
              }}
            />
          </View>
        )}
      </View>
    </View>
  );
};

export default React.memo(HomeContent);
